import { useAuth } from '@/hooks/useAuth';

const MASQUERADE_USER_KEY = 'fundingos_masquerade_user';

/** Check if currently masquerading */
export function isMasquerading(): boolean {
  return localStorage.getItem(MASQUERADE_USER_KEY) !== null;
}

/** Get masquerade user info */
export function getMasqueradeUser(): { email: string; role: string; first_name: string; last_name: string } | null {
  const raw = localStorage.getItem(MASQUERADE_USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/** Start masquerading as another user (cookie is set by the API response) */
export function startMasquerade(user: { email: string; role: string; first_name: string; last_name: string }): void {
  localStorage.setItem(MASQUERADE_USER_KEY, JSON.stringify(user));
}

/** Stop masquerading — redirects to login so admin can re-authenticate */
export function stopMasquerade(): void {
  localStorage.removeItem(MASQUERADE_USER_KEY);
}

/** The masquerade banner shown at the top of the page */
export function MasqueradeBanner() {
  const { user } = useAuth();

  if (!isMasquerading()) return null;

  const masqueradeUser = getMasqueradeUser();
  const displayName = masqueradeUser
    ? `${masqueradeUser.first_name} ${masqueradeUser.last_name} (${masqueradeUser.email})`
    : user?.email ?? 'another user';
  const displayRole = masqueradeUser?.role ?? user?.role ?? '';

  const handleExit = () => {
    stopMasquerade();
    window.location.href = '/login';
  };

  return (
    <div className="fixed top-0 left-0 right-0 z-[9999] bg-amber-500 text-black px-4 py-2 text-center text-sm font-medium flex items-center justify-center gap-3" style={{ minHeight: '40px' }}>
      <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
      </svg>
      <span>
        Masquerading as <strong>{displayName}</strong>
        {displayRole && <span className="ml-1 px-1.5 py-0.5 rounded bg-black/15 text-xs uppercase">{displayRole}</span>}
      </span>
      <button type="button"
        onClick={handleExit}
        className="ml-2 px-3 py-1 rounded-md bg-black/20 hover:bg-black/30 text-sm font-semibold transition-colors"
      >
        Exit Masquerade
      </button>
    </div>
  );
}
